import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Paragraph,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { LoadingParagraph } from '@zg-rentals/loading-components';
import { IconPlusOutline } from '@zillow/constellation-icons';
import { validate } from 'email-validator';
import type { InviteGuarantorWebApiResponseDto } from '@zg-rentals/ts-capps-client';
import {
  CappsRenterGuarantorInvitationsWebApiHandler_getGuarantorInvite_queryOptions,
  useCappsRenterGuarantorInvitationsWebApiHandler_inviteGuarantor,
  useCappsRenterGuarantorInvitationsWebApiHandler_removeGuarantor,
} from '@zg-rentals/ts-capps-client-react-query';
import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useApplicationContext } from '../../../../contexts/ApplicationContext';
import type { CurrentIncome } from '../../../../models/rental-application';
import { getNumberFromString } from '../../../../utils/formatters';
import { useWatch } from 'react-hook-form';
import { GUARANTOR_INVITE_TAG, useGuarantorInvite } from '../../../../hooks/useGuarantorInvite';
import { GuarantorSummary } from '../../../Guarantor/GuarantorSummary';

export function isIncomeBelowRequirement(
  currentIncomes: Array<CurrentIncome | null> | null | undefined,
  monthlyPayment: string | null | undefined,
) {
  const monthlyRent = getNumberFromString(monthlyPayment);
  if (monthlyRent <= 0) {
    return false;
  }

  const monthlyIncome = (currentIncomes ?? []).reduce(
    (total, income) => total + getNumberFromString(income?.amount),
    0,
  );

  return monthlyIncome * 12 < monthlyRent * 40;
}

export function GuarantorSection({ applicationId }: { applicationId: string }) {
  const [isAdding, setIsAdding] = useState(false);
  const currentIncomes = useWatch({ name: 'currentIncomes' });
  const { listing } = useApplicationContext();
  const showIncomeWarning = isIncomeBelowRequirement(currentIncomes, listing.rentalListing?.monthlyPayment);
  const { data, isError, isFetching, isLoading, refetch } = useGuarantorInvite(applicationId);
  const guarantorInvite = data?.guarantorInvite;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', marginTop: 'default' }}>
      {showIncomeWarning ? (
        <Alert tone="warning">
          Your income may not meet this property’s requirement. Adding a guarantor can strengthen your application.
        </Alert>
      ) : null}
      {isLoading ? (
        <Box
          role="status"
          aria-label="Loading guarantor"
          css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', padding: 'default' }}
        >
          <LoadingParagraph isLoading loadingWidth="40%">
            Loading guarantor
          </LoadingParagraph>
          <LoadingParagraph isLoading loadingWidth="60%">
            Loading guarantor email
          </LoadingParagraph>
        </Box>
      ) : guarantorInvite ? (
        <Guarantor applicationId={applicationId} guarantor={guarantorInvite} />
      ) : isError ? (
        <Alert
          tone="critical"
          actionButton={
            <TextButton type="button" disabled={isFetching} onClick={() => void refetch()}>
              Try again
            </TextButton>
          }
        >
          We couldn’t load guarantor information
        </Alert>
      ) : (
        <Button
          fluid
          type="button"
          tone="brand"
          emphasis="outlined"
          icon={<IconPlusOutline />}
          onClick={() => setIsAdding(true)}
        >
          Add guarantor
        </Button>
      )}
      {isAdding && <AddGuarantorModal applicationId={applicationId} onClose={() => setIsAdding(false)} />}
    </Box>
  );
}

function Guarantor({
  applicationId,
  guarantor,
}: {
  applicationId: string;
  guarantor: InviteGuarantorWebApiResponseDto;
}) {
  const { enqueueToast } = useToast();
  const removeGuarantor = useCappsRenterGuarantorInvitationsWebApiHandler_removeGuarantor({
    meta: {
      invalidates: [GUARANTOR_INVITE_TAG],
      context: { api: 'cappsRenterGuarantorInvitations', details: 'removeGuarantor' },
    },
  });

  async function onRemove() {
    try {
      await removeGuarantor.mutateAsync({ postData: { applicationId } });
    } catch (error) {
      logError({ error, context: 'Guarantor - removeGuarantor error' });
      enqueueToast(<Toast tone="critical">We couldn’t remove the guarantor. Try again.</Toast>);
    }
  }

  return (
    <Box
      role="group"
      aria-label={`Guarantor ${guarantor.firstName} ${guarantor.lastName}`}
      aria-busy={removeGuarantor.isPending}
      css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <GuarantorSummary guarantor={guarantor} />
      <TextButton type="button" disabled={removeGuarantor.isPending} onClick={onRemove}>
        Remove
      </TextButton>
    </Box>
  );
}

function AddGuarantorModal({ applicationId, onClose }: { applicationId: string; onClose: () => void }) {
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string }>({});
  const { application } = useApplicationContext();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const inviteGuarantor = useCappsRenterGuarantorInvitationsWebApiHandler_inviteGuarantor({
    meta: {
      invalidates: [GUARANTOR_INVITE_TAG],
      context: { api: 'cappsRenterGuarantorInvitations', details: 'inviteGuarantor' },
    },
  });

  async function onSendInvite() {
    const firstName = firstNameRef.current?.value.trim() ?? '';
    const lastName = lastNameRef.current?.value.trim() ?? '';
    const email = emailRef.current?.value.trim().toLowerCase() ?? '';
    const nextErrors = {
      firstName: firstName ? undefined : 'First name is required',
      lastName: lastName ? undefined : 'Last name is required',
      email: !email
        ? 'Email address is required'
        : !validate(email)
          ? 'Enter a valid email address'
          : email === String(application.answers?.email).toLowerCase()
            ? 'You cannot add yourself as a guarantor'
            : undefined,
    };

    if (nextErrors.firstName || nextErrors.lastName || nextErrors.email) {
      setErrors(nextErrors);
      return;
    }

    try {
      const invitedGuarantor = await inviteGuarantor.mutateAsync({
        postData: {
          applicationId,
          firstName,
          lastName,
          email,
        },
      });
      queryClient.setQueryData(
        CappsRenterGuarantorInvitationsWebApiHandler_getGuarantorInvite_queryOptions({ applicationId }).queryKey,
        { guarantorInvite: invitedGuarantor },
      );
      onClose();
    } catch (error) {
      logError({ error, context: 'AddGuarantorModal - inviteGuarantor error' });
      enqueueToast(<Toast tone="critical">We couldn’t invite the guarantor. Try again.</Toast>);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={2}>Add a guarantor</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: 'default' }}>
            We’ll email them a link to complete their own short guarantor application. They’ll need to verify their
            identity and income.
          </Paragraph>
          <FormField
            label={<Label>First name</Label>}
            control={<Input ref={firstNameRef} maxLength={32} />}
            description={errors.firstName ? <FormHelp>{errors.firstName}</FormHelp> : undefined}
            error={!!errors.firstName}
            required
            css={{ marginBottom: 'default' }}
          />
          <FormField
            label={<Label>Last name</Label>}
            control={<Input ref={lastNameRef} maxLength={32} />}
            description={errors.lastName ? <FormHelp>{errors.lastName}</FormHelp> : undefined}
            error={!!errors.lastName}
            required
            css={{ marginBottom: 'default' }}
          />
          <FormField
            label={<Label>Email address</Label>}
            control={<Input ref={emailRef} maxLength={100} type="email" />}
            description={errors.email ? <FormHelp>{errors.email}</FormHelp> : undefined}
            error={!!errors.email}
            required
          />
        </>
      }
      footer={
        <ButtonGroup aria-label="Add guarantor actions">
          <TextButton type="button" onClick={onClose}>
            Cancel
          </TextButton>
          <Button
            type="button"
            tone="brand"
            emphasis="filled"
            disabled={inviteGuarantor.isPending}
            loading={inviteGuarantor.isPending}
            onClick={onSendInvite}
          >
            Send invite
          </Button>
        </ButtonGroup>
      }
    />
  );
}
